import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { useClient } from '../../apollo';
import {
  requestError,
  requestInit,
  requestStart,
  requestSuccess,
  setErrorResponse,
  RequestState,
} from '../../auth/hooks/useRequestState';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import {
  AnswerSavePayload,
  copyProgramAnswersToReport as copyProgramAnswersToReportApi,
  deleteProgramAnswer as deleteProgramAnswerApi,
  fetchProgramQuestions,
  fetchProgramSummary,
  saveProgramAnswer as saveProgramAnswerApi,
  updateProgramAnswer as updateProgramAnswerApi,
  updateProgramFields,
} from './api';
import {
  AnswerInput,
  ProgramAnswersById,
  ProgramQuestionsResult,
  ProgramQuestionsSection,
  ProgramQuestionWithAnswer,
  ProgramSummary,
  QuestionStatus,
  SectionStatus,
} from './types';
import { useServiceCollections } from '../../apollo/pageHooks/useAllServiceCollections';
import {
  applyImpactSummaryFlow,
  applyProgramTagsToQuestions,
  findQuestion,
  firstUnansweredIn,
  resolveServiceGoal,
  getQuestionStatus,
  getSectionStatus,
  pickInitialQuestionId,
  sectionKey,
  validateAnswerInput,
} from './utils';
import { getHelpText } from 'npo/pages/ReportBuilder/Components/ImpactSummary/HelperComponents';
import { logicModalImpactSummary } from 'npo/pages/ReportBuilder/Components/ImpactSummary/utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useAllIndicatorsMin } from '../../apollo/pageHooks/useAllIndicators';
import {
  getImpactIndicators,
  REACH_STAGE_ID,
} from 'npo/pages/NPOFeedback/MakeEdits/utils';
import TrueImpactLoading from '../../components/TrueImpactLoading';
import { useOptionalPrograms } from 'npo/pages/ProgramsAndReports/ProgramsContext';

type ImpactIndicators = ReturnType<typeof getImpactIndicators>;

type SaveResult =
  | { success: true; nextUnansweredQuestionId: number | null }
  | { success: false; error: string };

const asError = (message: string, status = 500) =>
  setErrorResponse({ status, message, isAxiosError: false });

const fail = (error: string): SaveResult => ({ success: false, error });

/**
 * Merge a save-mutation payload into an existing programQuestions
 * result: replaces the affected section (if the backend returned it),
 * refreshes program-wide completed / nextUnansweredQuestionId, and
 * recomputes the top-level answeredCount from section counters.
 * Falls back to the previous state for any field the payload omits.
 */
const applySavePayload = (
  prev: ProgramQuestionsResult,
  payload: AnswerSavePayload,
): ProgramQuestionsResult => {
  const nextSections = payload.section
    ? prev.sections.map(s =>
        s.section === payload.section!.section ? payload.section! : s,
      )
    : prev.sections;
  const answeredCount = nextSections.reduce((n, s) => n + s.answered, 0);
  return {
    ...prev,
    sections: nextSections,
    answeredCount,
    completed: payload.completed != null ? payload.completed : prev.completed,
    nextUnansweredQuestionId:
      payload.nextUnansweredQuestionId !== undefined
        ? payload.nextUnansweredQuestionId
        : prev.nextUnansweredQuestionId,
  };
};

interface ProgramContextProps {
  programId: number | null;
  program: RequestState<ProgramSummary>;
  programName: string;
  questions: RequestState<ProgramQuestionsResult>;

  /** Currently-selected question in the wizard. */
  activeQuestionId: number | null;
  setActiveQuestionId: (id: number | null) => void;

  /** True once the user attempted to submit while incomplete. */
  attemptedSubmit: boolean;
  setAttemptedSubmit: (v: boolean) => void;

  /** Derived helpers. */
  isAllComplete: boolean;
  sectionStatus: (section: ProgramQuestionsSection) => SectionStatus;
  questionStatus: (question: ProgramQuestionWithAnswer) => QuestionStatus;
  findQuestionById: (
    id: number | null | undefined,
  ) => {
    section: ProgramQuestionsSection;
    question: ProgramQuestionWithAnswer;
  } | null;
  firstUnansweredInSection: (
    section: ProgramQuestionsSection | null | undefined,
  ) => number | null;
  validate: (
    question: ProgramQuestionWithAnswer,
    input: AnswerInput,
  ) => string | null;
  getHelperText: (
    sectionKey: string | null | undefined,
    questionId: number,
  ) => React.ReactNode;

  /** All filled answers keyed by questionId, including their sectionKey. */
  answers: ProgramAnswersById;

  /**
   * When the wizard runs on top of a *report* (snapshot) but a question
   * is flagged as `isProgramQuestion`, the underlying answer lives on
   * the owning Program profile and is not part of the report's own
   * answer set. This lookup exposes that program answer purely so the
   * pane can pre-fill the disabled input. Returns null / undefined when
   * unavailable. Program-profile ProgramProvider does not implement it.
   */
  getProgramProfileAnswer?: (
    questionId: number,
  ) => import('./types').ProgramAnswer | null | undefined;

  /** Flat questionId -> text map. Same data as `answers`, shaped for
   *  report-builder helpers that expect `{ [id]: string | undefined }`. */
  answersTextById: { [id: number]: string | undefined };

  /** Flat `Answer[]` shape consumed by `getHelpText` and other
   *  report-builder utilities. */
  answersArray: Answer[];

  /** Social-impact (succeed, stage 3) indicators derived from Q40003. */
  allSucceedIndicators: ImpactIndicators | undefined;
  /** Reach (stage 5) indicators derived from Q40003. */
  allReachIndicators: ImpactIndicators | undefined;
  loadingIndicators: boolean;

  /** API actions. */
  reloadQuestions: () => Promise<void>;
  reloadProgram: () => Promise<void>;
  saveAnswer: (
    question: ProgramQuestionWithAnswer,
    input: AnswerInput,
  ) => Promise<SaveResult>;
  deleteAnswer: (answerDbId: number) => Promise<SaveResult>;
  updateProgramName: (name: string) => Promise<SaveResult>;
  submitProgram: () => Promise<SaveResult>;
  reopenProgram: () => Promise<SaveResult>;
  copyProgramAnswersToReport: (reportId: number) => Promise<SaveResult>;
  saveProgramAnswer?: (
    questionId: number,
    input: AnswerInput,
  ) => Promise<SaveResult>;

  /** Fine-grained per-mutation loading state. */
  saving: RequestState<{ questionId: number }>;
  savingProgramAnswer?: RequestState<{ questionId: number }>;
  submitting: RequestState<{ isCompleted: boolean }>;
  renaming: RequestState<{ name: string }>;
}

const noop: () => Promise<SaveResult> = async () => ({
  success: false as const,
  error: 'ProgramProvider is not mounted.',
});

export const ProgramContext = createContext<ProgramContextProps>({
  programId: null,
  program: requestInit(),
  programName: 'your program',
  questions: requestInit(),
  activeQuestionId: null,
  setActiveQuestionId: noop,
  attemptedSubmit: false,
  setAttemptedSubmit: noop,
  isAllComplete: false,
  sectionStatus: () => 'incomplete',
  questionStatus: () => 'incomplete',
  findQuestionById: () => null,
  firstUnansweredInSection: () => null,
  validate: () => null,
  getHelperText: () => null,
  answers: {},
  answersTextById: {},
  answersArray: [],
  allSucceedIndicators: undefined,
  allReachIndicators: undefined,
  loadingIndicators: false,
  reloadQuestions: async () => undefined,
  reloadProgram: async () => undefined,
  saveAnswer: noop,
  deleteAnswer: noop,
  updateProgramName: noop,
  submitProgram: noop,
  reopenProgram: noop,
  copyProgramAnswersToReport: noop,
  saving: requestInit(),
  submitting: requestInit(),
  renaming: requestInit(),
});

export const useProgram = () => useContext(ProgramContext);

interface ProgramProviderProps {
  programId: number | null;
  initialQuestionId?: number | null;
  programName?: string;
  children: React.ReactNode;
}

const errorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

const ProgramProvider: React.FC<ProgramProviderProps> = ({
  programId,
  initialQuestionId,
  programName: providedProgramName = 'your program',
  children,
}) => {
  const client = useClient();
  const [program, loadProgram, setProgram] = useGraphQLRequest<
    ProgramSummary
  >();
  const [questions, loadQuestions, setQuestions] = useGraphQLRequest<
    ProgramQuestionsResult
  >();
  const [activeQuestionId, setActiveQuestionId] = useState<number | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState<boolean>(false);
  const [saving, setSaving] = useState<RequestState<{ questionId: number }>>(
    requestInit(),
  );
  const [submitting, setSubmitting] = useState<
    RequestState<{ isCompleted: boolean }>
  >(requestInit());
  const [renaming, setRenaming] = useState<RequestState<{ name: string }>>(
    requestInit(),
  );

  // When rendered inside the Programs & Reports list, keep that list's local
  // copy of this program in sync after mutations (e.g. submit / reopen) so
  // "Incomplete" badges, CTAs, and add-report gating update without a refetch.
  const programsListCtx = useOptionalPrograms();
  const updateProgramInList = programsListCtx?.updateProgramLocal;

  const reloadProgram = useCallback(async () => {
    if (!client || programId == null) return;
    await loadProgram(fetchProgramSummary(client, programId));
  }, [client, programId, loadProgram]);

  const reloadQuestions = useCallback(async () => {
    if (!client || programId == null) return;
    await loadQuestions(fetchProgramQuestions(client, programId));
  }, [client, programId, loadQuestions]);

  useEffect(() => {
    if (programId == null) return;
    reloadProgram();
    reloadQuestions();
  }, [programId, reloadProgram, reloadQuestions]);

  const programName =
    program.type === 'REQUEST_SUCCESS' && program.data.name
      ? program.data.name
      : providedProgramName;

  const questionsRaw =
    questions.type === 'REQUEST_SUCCESS' ? questions.data : null;

  // Feeds the `<Service_goal>` placeholder in question text (e.g. Q40021).
  const serviceCollections = useServiceCollections({});

  const questionsData = useMemo(() => {
    const answerText = (id: number) =>
      findQuestion(questionsRaw, id)?.question.answer?.text || null;
    const serviceGoal = resolveServiceGoal(
      serviceCollections.type === 'REQUEST_SUCCESS'
        ? serviceCollections.data
        : null,
      answerText(40002),
      answerText(40003),
    );
    return applyImpactSummaryFlow(
      applyProgramTagsToQuestions(questionsRaw, programName, { serviceGoal }),
    );
  }, [questionsRaw, programName, serviceCollections]);

  const questionsForContext = useMemo<RequestState<ProgramQuestionsResult>>(
    () =>
      questions.type === 'REQUEST_SUCCESS' && questionsData
        ? requestSuccess<ProgramQuestionsResult>(questionsData)
        : questions,
    [questions, questionsData],
  );

  useEffect(() => {
    if (questionsData) {
      setActiveQuestionId(prev => {
        if (prev != null) return prev;
        if (initialQuestionId != null) {
          const exists = questionsData.sections.some(s =>
            s.questions.some(q => q.questionId === initialQuestionId),
          );
          if (exists) return initialQuestionId;
        }
        return pickInitialQuestionId(questionsData);
      });
    }
  }, [questionsData, initialQuestionId]);

  const isAllComplete = Boolean(questionsData?.completed);

  const sectionStatus = useCallback(
    (section: ProgramQuestionsSection) =>
      getSectionStatus(section, attemptedSubmit),
    [attemptedSubmit],
  );

  const questionStatus = useCallback(
    (question: ProgramQuestionWithAnswer) =>
      getQuestionStatus(question, attemptedSubmit),
    [attemptedSubmit],
  );

  /**
   * O(1) lookup index built once per `questionsData` change. Replaces the
   * previous O(sections * questions) linear scan performed by `findQuestion`
   * on every call.
   */
  const questionIndex = useMemo(() => {
    const map = new Map<
      number,
      {
        section: ProgramQuestionsSection;
        question: ProgramQuestionWithAnswer;
      }
    >();
    questionsData?.sections.forEach(section => {
      section.questions.forEach(question => {
        map.set(question.questionId, { section, question });
      });
    });
    return map;
  }, [questionsData]);

  const findQuestionById = useCallback(
    (id: number | null | undefined) =>
      id == null ? null : questionIndex.get(id) ?? null,
    [questionIndex],
  );

  const firstUnansweredInSection = useCallback(
    (section: ProgramQuestionsSection | null | undefined) =>
      firstUnansweredIn(section),
    [],
  );

  const validate = useCallback(
    (question: ProgramQuestionWithAnswer, input: AnswerInput) =>
      validateAnswerInput(question, input),
    [],
  );

  /**
   * Answers derived from the questions tree, exposed in two shapes:
   *  - `answersArray`: flat `Answer[]` shape consumed by `getHelpText` and
   *    other report-builder utilities.
   *  - `answers`: keyed by `questionId` so callers can look an answer up
   *    without re-walking the tree. Each entry also carries the owning
   *    `sectionKey` for consumers that need cross-section context.
   * Recomputed only when the underlying questions data changes.
   */
  const { answers, answersArray, answersTextById } = useMemo<{
    answers: ProgramAnswersById;
    answersArray: Answer[];
    answersTextById: { [id: number]: string | undefined };
  }>(() => {
    const answersById: ProgramAnswersById = {};
    const textById: { [id: number]: string | undefined } = {};
    const collected: Answer[] = [];
    if (!questionsData)
      return {
        answers: answersById,
        answersArray: collected,
        answersTextById: textById,
      };
    questionsData.sections.forEach(section => {
      section.questions.forEach(q => {
        if (q.answer && q.answer.text != null) {
          collected.push({
            questionId: q.questionId,
            text: q.answer.text,
          } as Answer);
          answersById[q.questionId] = {
            questionId: q.questionId,
            sectionKey: q.section,
            text: q.answer.text,
          };
          textById[q.questionId] = q.answer.text;
        }
      });
    });
    return {
      answers: answersById,
      answersArray: collected,
      answersTextById: textById,
    };
  }, [questionsData]);

  const {
    data: indicators,
    loading: loadingIndicators,
  } = useAllIndicatorsMin();

  const allSucceedIndicators = useMemo<ImpactIndicators | undefined>(() => {
    const serviceAnswer = answers[40003]?.text;
    return indicators && serviceAnswer
      ? getImpactIndicators(indicators, serviceAnswer)
      : undefined;
  }, [indicators, answers]);

  const allReachIndicators = useMemo<ImpactIndicators | undefined>(() => {
    const serviceAnswer = answers[40003]?.text;
    return indicators && serviceAnswer
      ? getImpactIndicators(indicators, serviceAnswer, REACH_STAGE_ID)
      : undefined;
  }, [indicators, answers]);

  const getHelperText = useCallback(
    (
      sectionKeyValue: string | null | undefined,
      questionId: number,
    ): React.ReactNode => {
      // Only impact-summary questions carry cross-answer helper text today.
      if (sectionKey(sectionKeyValue) !== 'impact_summary') return null;
      const cause = questionId === 40004 ? answers[40003]?.text : undefined;
      const mainBeneficiaryText = cause
        ? (logicModalImpactSummary as Record<string, string>)[cause]
        : undefined;
      return getHelpText(
        questionId,
        answersArray,
        null,
        mainBeneficiaryText,
        undefined,
      );
    },
    [answers, answersArray],
  );

  const saveAnswer = useCallback(
    async (
      question: ProgramQuestionWithAnswer,
      input: AnswerInput,
    ): Promise<SaveResult> => {
      if (!client || programId == null) {
        return fail('Not ready.');
      }
      const validationError = validateAnswerInput(question, input);
      if (validationError) {
        return fail(validationError);
      }
      setSaving(requestStart());
      try {
        const existingId = question.answer?.dbId ?? null;
        let payload: AnswerSavePayload | undefined;
        if (existingId) {
          const res = await updateProgramAnswerApi(client, existingId, input);
          payload = res.data ?? undefined;
        } else {
          const res = await saveProgramAnswerApi(
            client,
            programId,
            question.questionId,
            input,
          );
          payload = res.data ?? undefined;
        }
        if (!payload?.success) {
          setSaving(requestError(asError('Failed to save answer.')));
          return fail('Failed to save answer.');
        }
        setSaving(requestSuccess({ questionId: question.questionId }));
        // Patch the cache from the mutation payload — no refetch needed.
        const merged = payload;
        if (questions.type === 'REQUEST_SUCCESS') {
          setQuestions(
            requestSuccess<ProgramQuestionsResult>(
              applySavePayload(questions.data, merged),
            ),
          );
        }
        return {
          success: true,
          nextUnansweredQuestionId: payload.nextUnansweredQuestionId,
        };
      } catch (err) {
        const message = errorMessage(err, 'Failed to save answer.');
        setSaving(requestError(asError(message)));
        return fail(message);
      }
    },
    [client, programId, questions, setQuestions],
  );

  const saveProgramAnswer = useCallback(
    async (questionId: number, input: AnswerInput): Promise<SaveResult> => {
      const found = questionIndex.get(questionId);
      if (!found) return fail('Question not found.');
      return saveAnswer(found.question, input);
    },
    [questionIndex, saveAnswer],
  );

  const deleteAnswer = useCallback(
    async (answerDbId: number): Promise<SaveResult> => {
      if (!client) return fail('Not ready.');
      try {
        const res = await deleteProgramAnswerApi(client, answerDbId);
        if (!res.data?.success) {
          return fail('Failed to delete answer.');
        }
        await reloadQuestions();
        return { success: true, nextUnansweredQuestionId: null };
      } catch (err) {
        return fail(errorMessage(err, 'Failed to delete answer.'));
      }
    },
    [client, reloadQuestions],
  );

  const updateProgramName = useCallback(
    async (name: string): Promise<SaveResult> => {
      if (!client || programId == null) {
        return fail('Not ready.');
      }
      if (!name.trim()) {
        return fail('Program name is required.');
      }
      setRenaming(requestStart());
      try {
        const res = await updateProgramFields(client, programId, {
          name: name.trim(),
        });
        if (!res.data?.success) {
          setRenaming(requestError(asError('Failed to update program name.')));
          return fail('Failed to update program name.');
        }
        const returnedProgram = res.data.program;
        if (returnedProgram) {
          setProgram(
            requestSuccess<ProgramSummary>({
              id: returnedProgram.dbId,
              dbId: returnedProgram.dbId,
              name: returnedProgram.name,
              isCompleted: Boolean(returnedProgram.isCompleted),
            }),
          );
        } else if (program.type === 'REQUEST_SUCCESS') {
          setProgram(
            requestSuccess<ProgramSummary>({
              ...program.data,
              name: name.trim(),
            }),
          );
        }
        setRenaming(requestSuccess({ name: name.trim() }));
        return { success: true, nextUnansweredQuestionId: null };
      } catch (err) {
        const message = errorMessage(err, 'Failed to update program name.');
        setRenaming(requestError(asError(message)));
        return fail(message);
      }
    },
    [client, programId, program, setProgram],
  );

  const setCompletion = useCallback(
    async (isCompleted: boolean): Promise<SaveResult> => {
      if (!client || programId == null) {
        return fail('Not ready.');
      }
      setSubmitting(requestStart());
      try {
        const res = await updateProgramFields(client, programId, {
          isCompleted,
        });
        if (!res.data?.success) {
          setSubmitting(requestError(asError('Failed to update program.')));
          return fail('Failed to update program.');
        }
        const returnedProgram = res.data.program;
        if (returnedProgram) {
          setProgram(
            requestSuccess<ProgramSummary>({
              id: returnedProgram.dbId,
              dbId: returnedProgram.dbId,
              name: returnedProgram.name,
              isCompleted: Boolean(returnedProgram.isCompleted),
            }),
          );
        } else if (program.type === 'REQUEST_SUCCESS') {
          setProgram(
            requestSuccess<ProgramSummary>({
              ...program.data,
              isCompleted,
            }),
          );
        }
        setSubmitting(requestSuccess({ isCompleted }));
        if (updateProgramInList && programId != null) {
          updateProgramInList(programId, prev => ({
            ...prev,
            program: { ...prev.program, isCompleted },
          }));
        }
        return { success: true, nextUnansweredQuestionId: null };
      } catch (err) {
        const message = errorMessage(err, 'Failed to update program.');
        setSubmitting(requestError(asError(message)));
        return fail(message);
      }
    },
    [client, programId, program, setProgram, updateProgramInList],
  );

  const submitProgram = useCallback(async (): Promise<SaveResult> => {
    if (!isAllComplete) {
      setAttemptedSubmit(true);
      return fail(
        'Some sections are still incomplete. Fill the highlighted questions before submitting.',
      );
    }
    return setCompletion(true);
  }, [isAllComplete, setCompletion]);

  const reopenProgram = useCallback(() => setCompletion(false), [
    setCompletion,
  ]);

  const copyProgramAnswersToReport = useCallback(
    async (reportId: number): Promise<SaveResult> => {
      if (!client) return fail('Not ready.');
      try {
        const res = await copyProgramAnswersToReportApi(client, reportId);
        if (!res.data?.success) {
          return fail('Failed to copy answers.');
        }
        return { success: true, nextUnansweredQuestionId: null };
      } catch (err) {
        return fail(errorMessage(err, 'Failed to copy answers.'));
      }
    },
    [client],
  );

  const value = useMemo<ProgramContextProps>(
    () => ({
      programId,
      program,
      programName,
      questions: questionsForContext,
      activeQuestionId,
      setActiveQuestionId,
      attemptedSubmit,
      setAttemptedSubmit,
      isAllComplete,
      sectionStatus,
      questionStatus,
      findQuestionById,
      firstUnansweredInSection,
      validate,
      getHelperText,
      answers,
      answersTextById,
      answersArray,
      allSucceedIndicators,
      allReachIndicators,
      loadingIndicators,
      reloadQuestions,
      reloadProgram,
      saveAnswer,
      saveProgramAnswer,
      deleteAnswer,
      updateProgramName,
      submitProgram,
      reopenProgram,
      copyProgramAnswersToReport,
      saving,
      savingProgramAnswer: saving,
      submitting,
      renaming,
    }),
    [
      programId,
      program,
      programName,
      questionsForContext,
      activeQuestionId,
      attemptedSubmit,
      isAllComplete,
      sectionStatus,
      questionStatus,
      findQuestionById,
      firstUnansweredInSection,
      validate,
      getHelperText,
      answers,
      answersTextById,
      answersArray,
      allSucceedIndicators,
      allReachIndicators,
      loadingIndicators,
      reloadQuestions,
      reloadProgram,
      saveAnswer,
      saveProgramAnswer,
      deleteAnswer,
      updateProgramName,
      submitProgram,
      reopenProgram,
      copyProgramAnswersToReport,
      saving,
      submitting,
      renaming,
    ],
  );

  if (loadingIndicators) {
    return <TrueImpactLoading />;
  }

  return (
    <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>
  );
};

export default ProgramProvider;
export { sectionKey };
export * from './types';

const HelperTextBox = styled.div`
  padding: ${SPACERS.small};
  background-color: ${COLORS.ctaPrimaryBackground};
  border-radius: 4px;
  margin-bottom: ${SPACERS.medium};
`;

interface HelperTextProps {
  sectionKey: string | null | undefined;
  questionId: number;
}

export const HelperText: React.FC<HelperTextProps> = ({
  sectionKey: sectionKeyValue,
  questionId,
}) => {
  const { getHelperText } = useProgram();
  const content = getHelperText(sectionKeyValue, questionId);
  if (!content) return null;
  return (
    <HelperTextBox>
      <TrueImpactText variant="body-small">{content}</TrueImpactText>
    </HelperTextBox>
  );
};
